import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, finalize, map, of } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { friendlyError } from '../../utils/friendly-error';
import { DialogService } from '../../services/dialog.service';
import { Novelty, NoveltyService } from '../../services/novelty.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { PaginationComponent } from '../ui/pagination.component';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { ManagementRecordCardComponent } from '../ui/management-record-card.component';
import { finishLoadingAfter } from '../../utils/loading';
import { formatLongDate } from '../../utils/format';
import { UiStateComponent } from '../ui/ui-state.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { ThemeService } from '../../services/theme.service';
import { toneForTheme } from '../../utils/tone-color';

type StatusFilter = '' | 'unread' | 'read' | 'resolved' | 'expired';
type PriorityFilter = '' | 'low' | 'normal' | 'high' | 'critical';
type CategoryFilter = '' | 'reservations' | 'payments' | 'subscriptions' | 'customers' | 'spaces' | 'settings' | 'employees';

@Component({
  selector: 'app-novedades',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, UiSkeletonComponent, ManagementRecordCardComponent, UiStateComponent, CooldownBubbleDirective],
  template: `
<section class="nv-shell paginated-module">
  <header class="hero">
    <div>
      <span class="hero-badge"><i class="bi bi-clipboard-data"></i> Novedades del Negocio</span>
      <h2>Actividad operativa</h2>
      <p class="hero-sub">Reservas, pagos, suscripciones y eventos importantes</p>
    </div>
    <div class="hero-stats">
      <div class="stat-pill"><i class="bi bi-box-seam"></i> <strong>{{ total() }}</strong> <small>total</small></div>
      <div *ngIf="unread()" class="stat-pill unread-pill"><i class="bi bi-circle-fill"></i> <strong>{{ unread() }}</strong> <small>sin leer</small></div>
      <button class="tb-btn tb-primary" (click)="markAll()" [disabled]="!unread() || markAllBusy()" cooldownBubble data-cooldown-key="novedades-mark-all">
        <span *ngIf="markAllBusy()" class="button-spinner"></span>
        <i *ngIf="!markAllBusy()" class="bi bi-check-lg"></i>
        {{ markAllBusy() ? 'Actualizando' : 'Marcar todo leído' }}
      </button>
    </div>
  </header>

  <div class="filters">
    <div class="ff search-ff">
      <label for="novelty-search">Buscar</label>
      <div class="search-control">
        <svg class="search-symbol" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5"></circle>
          <path d="m16 16 4 4"></path>
        </svg>
        <input id="novelty-search" type="search" [(ngModel)]="search" (keyup.enter)="applyFilters()" placeholder="Título, descripción o referencia">
      </div>
    </div>
    <div class="ff"><label><i class="bi bi-pin"></i> Estado</label>
      <select [(ngModel)]="status">
        <option value="">Todos</option><option value="unread">No le&iacute;das</option><option value="read">Le&iacute;das</option><option value="resolved">Resueltas</option><option value="expired">Expiradas</option>
      </select>
    </div>
    <div class="ff"><label><i class="bi bi-lightning-fill"></i> Prioridad</label>
      <select [(ngModel)]="priority">
        <option value="">Todas</option><option value="critical">Cr&iacute;tica</option><option value="high">Alta</option><option value="normal">Normal</option><option value="low">Baja</option>
      </select>
    </div>
    <div class="ff"><label><i class="bi bi-folder"></i> Categor&iacute;a</label>
      <select [(ngModel)]="category">
        <option value="">Todas</option><option value="reservations">Reservas</option><option value="payments">Pagos</option><option value="subscriptions">Suscripciones</option><option value="customers">Clientes</option><option value="spaces">Espacios</option><option value="settings">Ajustes</option><option value="employees">Empleados</option>
      </select>
    </div>
    <div class="ff"><label><i class="bi bi-list-ol"></i> Registros</label>
      <select [ngModel]="limit" (ngModelChange)="onPageSizeChange($event)" [disabled]="loading()">
        <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }} por página</option>
      </select>
    </div>
      <button class="btn-primary" (click)="applyFilters()" [disabled]="loading()" cooldownBubble data-cooldown-key="novedades-filter"><span *ngIf="loading() && activeRequest()==='filter'" class="button-spinner"></span><i *ngIf="!(loading() && activeRequest()==='filter')" class="bi bi-funnel"></i> {{ loading() && activeRequest()==='filter' ? 'Filtrando' : 'Filtrar' }}</button>
    <button class="btn-ghost" (click)="resetFilters()" [disabled]="loading()" title="Restablecer filtros" aria-label="Restablecer filtros"><span *ngIf="loading() && activeRequest()==='reset'" class="button-spinner dark"></span><i *ngIf="!(loading() && activeRequest()==='reset')" class="bi bi-arrow-clockwise"></i></button>
  </div>

  <p class="msg msg-err" *ngIf="error()" role="alert"><i class="bi bi-exclamation-triangle-fill"></i> {{ error() }}</p>

  <app-ui-skeleton *ngIf="loading()" variant="table" [count]="6" [columnCount]="4" [compact]="true" label="Cargando novedades"></app-ui-skeleton>

  <app-ui-state *ngIf="!loading() && !visibleItems().length" tone="empty" kubo="exito" title="Estás al día" message="No hay novedades para los filtros seleccionados. Te avisaré en cuanto llegue algo nuevo."></app-ui-state>

  <div class="nv-directory" *ngIf="!loading() && visibleItems().length">
    <app-management-record-card *ngFor="let item of visibleItems(); trackBy: trackById"
      [clickable]="true"
      [tone]="item.priority === 'critical' ? 'inactive' : 'default'"
      kind="admin"
      [status]="item.priority === 'critical' ? 'danger' : item.priority === 'high' ? 'warning' : item.priority === 'low' ? 'neutral' : 'info'"
      [columns]="'48px minmax(260px,2.2fr) minmax(130px,.85fr) minmax(100px,.6fr)'"
      (recordActivate)="open(item)">
      <span recordIcon [style.color]="categoryColor(item.category)" [style.background]="categoryColorSoft(item.category)" aria-hidden="true"><i [class]="categoryIcon(item.category)"></i></span>
      <div recordPrimary>
        <small>{{ categoryLabel(item.category) }}</small>
        <strong [class.is-unread]="item.status==='unread'">
          <span class="unread-dot" *ngIf="item.status==='unread'" aria-hidden="true"></span>{{ normalizeNoveltyText(item.title) }}
        </strong>
        <span class="clamp-1">{{ normalizeNoveltyText(item.summary) }}</span>
      </div>
      <div recordSummary>
        <small>CUÁNDO</small>
        <strong>{{ relativeTime(item.created_at) }}</strong>
        <span>{{ statusLabel(item.status) }}<ng-container *ngIf="item.email_status==='sent'"> · Correo enviado</ng-container><ng-container *ngIf="item.email_status==='failed'"> · Correo fallido</ng-container></span>
      </div>
      <div recordStatus>
        <small>PRIORIDAD</small>
        <span class="prio-badge" [ngClass]="'prio-' + item.priority">{{ priorityLabel(item.priority) }}</span>
      </div>
    </app-management-record-card>
  </div>

  <app-pagination [page]="page()" [pages]="pages()" [total]="total()" [limit]="limit" [disabled]="loading()" itemLabel="novedades" ariaLabel="Paginación de novedades" (pageChange)="goToPage($event)"></app-pagination>
</section>

<div class="overlay" *ngIf="selected() as item" (click)="close()" role="dialog" aria-modal="true" [attr.aria-label]="normalizeNoveltyText(item.title)">
  <section class="modal" (click)="$event.stopPropagation()">
    <button type="button" class="modal-close" (click)="close()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
    <header class="novelty-modal-header">
      <span class="modal-category-icon category-{{ item.category }}" aria-hidden="true"><i [class]="categoryIcon(item.category)"></i></span>
      <div class="modal-heading-copy">
        <div class="modal-badges">
          <span class="badge badge-{{ item.category }}">{{ categoryLabel(item.category) }}</span>
          <span class="prio-badge prio-{{ item.priority }}">{{ priorityLabel(item.priority) }}</span>
        </div>
        <h2 class="modal-title">{{ normalizeNoveltyText(item.title) }}</h2>
      </div>
    </header>
    <div class="modal-desc">{{ detailDescription(item) }}</div>
    <div class="identity-grid" *ngIf="item.reservation_id || item.space_id">
      <div class="mc identity-card" *ngIf="item.reservation_id"><span><i class="bi bi-calendar-check"></i> Referencia de reserva</span><strong>{{ reservationDisplayName(item) }}</strong></div>
      <div class="mc identity-card" *ngIf="item.space_id"><span><i class="bi bi-building"></i> Espacio reservado</span><strong>{{ spaceDisplayName(item) }}</strong></div>
    </div>
    <div class="modal-grid">
      <div class="mc"><span>Estado</span><strong class="st-{{ item.status }}">{{ statusLabel(item.status) }}</strong></div>
      <div class="mc"><span><i class="bi bi-calendar3"></i> Fecha</span><strong>{{ formatLongDate(item.created_at) }}</strong></div>
      <div class="mc" *ngIf="item.payment_id"><span><i class="bi bi-credit-card"></i> Pago relacionado</span><strong>{{ paymentDisplayName(item) }}</strong></div>
      <div class="mc" *ngIf="item.subscription_id"><span><i class="bi bi-arrow-repeat"></i> Suscripción</span><strong>{{ subscriptionDisplayName(item) }}</strong></div>
      <div class="mc" *ngIf="shouldShowEmailStatus(item)"><span><i class="bi bi-envelope-fill"></i> Notificación por correo</span><strong><span class="em-{{ item.email_status }}">{{ emailStatusLabel(item.email_status!) }}</span></strong></div>
      <div class="mc" *ngIf="item.expires_at"><span><i class="bi bi-hourglass-split"></i> Expira</span><strong>{{ formatLongDate(item.expires_at) }}</strong></div>
    </div>
    <div class="meta-section" *ngIf="item.metadata && formatDetailKeys(item.metadata).length > 0">
      <h4><i class="bi bi-clipboard-data"></i> Detalles adicionales</h4>
      <div class="modal-grid meta-grid">
        <div class="mc" *ngFor="let key of formatDetailKeys(item.metadata)">
          <span>{{ formatDetailLabel(key) }}</span><strong>{{ formatDetailValue(key, item.metadata![key]) }}</strong>
        </div>
      </div>
    </div>
    <footer class="novelty-modal-actions">
      <button type="button" class="act-secondary" *ngIf="item.status!=='resolved'" (click)="resolve(item)" [disabled]="actionBusy()!==null" cooldownBubble data-cooldown-key="novedades-resolve">
        <span class="button-spinner dark" *ngIf="actionBusy()==='resolve'"></span>
        <i class="bi bi-check2-circle" *ngIf="actionBusy()!=='resolve'"></i>
        Marcar como resuelta
      </button>
      <button type="button" class="act-primary" *ngIf="targetModule(item)" (click)="goTarget(item)" [disabled]="actionBusy()!==null">
        <span class="button-spinner" *ngIf="actionBusy()==='navigate'"></span>
        <i class="bi bi-arrow-right-circle" *ngIf="actionBusy()!=='navigate'"></i>
        {{ targetActionLabel(item) }}
      </button>
    </footer>
  </section>
</div>
  `,
  styles: [`
:host{display:block;font-family:system-ui,-apple-system,sans-serif}
.nv-shell{display:grid;gap:1rem;max-width:none;margin:0 auto;padding:0;color:var(--ts-text);font-size:15px}
.hero{display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 1.15rem;border:1px solid var(--ts-border-sky-350-a25);border-radius:14px;background:var(--ts-bg-navy-850-32);box-shadow:none;animation:fadeIn .4s ease}
.hero-badge{font-size:.72rem;font-weight:700;color:var(--ts-text-blue-300-7);letter-spacing:.04em;display:block;margin-bottom:.1rem}
.hero-badge i{margin-right:.3rem}.hero h2{margin:.15rem 0;font-size:1.3rem;font-weight:750;color:var(--ts-text-blue-0-4)}
.hero-sub{margin:0;color:var(--ts-text-sky-300-15);font-size:.85rem}
.hero-stats{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;justify-content:flex-end}
.stat-pill{display:flex;align-items:center;gap:.3rem;padding:.4rem .65rem;border:1px solid var(--ts-border);border-radius:30px;background:var(--ts-wash-035);font-size:.82rem;transition:all .15s ease}
.stat-pill i{font-size:.78rem;color:var(--ts-text-blue-400-2)}.stat-pill strong{color:var(--ts-text-on-accent-20)}.stat-pill small{font-size:.62rem;color:var(--ts-text-blue-350);text-transform:uppercase}
.unread-pill{border-color:var(--ts-border-blue-400-a45-2);background:var(--ts-bg-blue-400-a10-2)}
.tb-btn{padding:.4rem .8rem;border:1px solid var(--ts-border-strong);border-radius:8px;background:var(--ts-bg-navy-850-33);color:var(--ts-text-blue-200-10);font-weight:600;font-size:.78rem;cursor:pointer;transition:all .15s ease;display:inline-flex;align-items:center;gap:.3rem}
.tb-btn:hover{background:var(--ts-bg-blue-400-a10);border-color:var(--ts-border-blue-400-a40-3);transform:translateY(-1px)}
.tb-primary{background:linear-gradient(135deg,var(--ts-bg-blue-450-6),var(--ts-bg-indigo-350-8));color:var(--ts-text);border-color:var(--ts-border-blue-400-4)}.tb-primary:hover{background:var(--ts-bg-blue-450)!important}.tb-primary:disabled{opacity:.4;cursor:not-allowed}
.filters{display:grid;grid-template-columns:minmax(220px,1.45fr) repeat(3,minmax(120px,.78fr)) minmax(145px,.8fr) auto 40px;align-items:end;gap:.65rem;padding:.85rem;border:1px solid var(--ts-border-sky-350-a24-2);border-radius:12px;background:var(--ts-bg-navy-850-32);box-shadow:0 10px 28px var(--ts-shadow-gray-1000-a8);animation:fadeIn .5s ease}
.ff{display:flex;flex-direction:column;gap:.35rem;min-width:0}
.ff label{font-size:.7rem;font-weight:600;color:var(--ts-text-sky-300-16)}
.ff label i{font-size:.64rem;margin-right:.2rem}
.ff input,.ff select{width:100%;min-width:0;height:40px;padding:.3rem .55rem;box-sizing:border-box;border:1px solid var(--ts-border-navy-700);border-radius:9px;font-size:.8rem;font-family:inherit;background:var(--ts-bg-navy-900);color:var(--ts-text);transition:all .15s ease;box-shadow:none}
.ff input:hover,.ff select:hover{border-color:var(--ts-border-sky-600-4)}
.ff input:focus,.ff select:focus{border-color:var(--ts-border-blue-300-3);outline:none;box-shadow:0 0 0 3px var(--ts-shadow-blue-300-a12)}
.ff input::placeholder{color:var(--ts-text-sky-450-12)}
.search-ff{position:static;flex:1;min-width:0}
.btn-primary,.btn-ghost{height:40px;border-radius:8px;font-size:.8rem;font-weight:600;cursor:pointer;transition:all .15s ease;white-space:nowrap;display:grid;place-items:center}
.btn-primary{padding:.35rem .85rem;border:1px solid var(--ts-border-blue-400-4);background:var(--ts-bg-blue-400);color:var(--ts-on-primary);gap:.35rem;grid-auto-flow:column}.btn-primary:hover{background:var(--ts-bg-blue-450);box-shadow:0 4px 12px var(--ts-shadow-blue-400-a20-4);transform:translateY(-1px)}
.btn-primary:disabled,.btn-ghost:disabled{cursor:not-allowed;opacity:.68;transform:none;box-shadow:none}
.btn-ghost{width:38px;padding:0;border:1px solid var(--ts-border-strong);background:var(--ts-surface-soft);color:var(--ts-link);font-size:.95rem}.btn-ghost:hover{background:var(--ts-bg-blue-400-a10)}
.msg{padding:.55rem .8rem;border-radius:8px;text-align:center;font-size:.84rem}
.msg-err{color:var(--ts-danger-text);background:color-mix(in srgb,var(--ts-danger) 12%, transparent);border:1px solid color-mix(in srgb,var(--ts-danger) 45%, transparent)}
@keyframes spin{to{transform:rotate(360deg)}}
.button-spinner{width:14px;height:14px;border:2px solid var(--ts-border-gray-0-a38);border-top-color:var(--ts-text);border-radius:50%;animation:spin .65s linear infinite}
.button-spinner.dark{border-color:var(--ts-border-blue-200-a30);border-top-color:var(--ts-border-blue-200)}

/* Directorio de novedades: misma tarjeta que Reservas y Clientes. */
.nv-directory{display:grid;gap:.7rem}
/* Las novedades sin leer resaltan con un punto de color y el título más
   claro — igual que un inbox — para que no todas las filas se vean iguales. */
::ng-deep [recordPrimary] strong.is-unread{color:var(--ts-text-on-accent-21);font-weight:800}
.unread-dot{display:inline-block;width:7px;height:7px;margin-right:.4rem;border-radius:50%;background:var(--ts-bg-blue-300-4);box-shadow:0 0 8px var(--ts-shadow-blue-300-a60);vertical-align:middle}

/* Badges y prioridades (usados en la tarjeta y en el modal de detalle). */
.badge,.prio-badge{display:inline-flex;align-items:center;padding:.12rem .45rem;border:1px solid currentColor;border-radius:5px;background:var(--ts-bg-gray-0-a4);font-size:.6rem;font-weight:650;text-transform:uppercase;letter-spacing:.04em;white-space:nowrap}
.badge-reservations{color:var(--ts-text-blue-250-17)}.badge-payments{color:var(--ts-text-green-350-4)}
.badge-subscriptions{color:var(--ts-text-violet-200)}.badge-customers{color:var(--ts-text-amber-300-3)}
.badge-spaces{color:var(--ts-text-cyan-300)}.badge-settings,.badge-employees{color:var(--ts-text-sky-250-17)}
.badge-administration{color:var(--ts-text-red-200-5)}.badge-owners{color:var(--ts-text-orange-300)}
.prio-badge{padding:.1rem .35rem;border-radius:4px;font-size:.58rem}
.prio-critical{color:var(--ts-text-red-200-6);background:var(--ts-bg-red-200-a8)}
.prio-high{color:var(--ts-text-amber-300-3);background:var(--ts-bg-amber-300-a8)}
.prio-normal,.prio-low{color:var(--ts-text-sky-250-18);background:var(--ts-bg-sky-250-a8)}
.prio-low{opacity:.7}

/* Modal de detalle. */
.overlay{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:1rem;background:var(--ts-bg-navy-950-a78-3);backdrop-filter:blur(4px);animation:fadeIn .15s ease}
.modal{--nv-accent:var(--ts-text-blue-300-8);position:relative;box-sizing:border-box;max-width:760px;max-height:min(88vh,820px);width:100%;overflow:auto;padding:1.25rem;border:1px solid var(--ts-border-navy-700);border-radius:14px;background:var(--ts-bg-navy-850-33);box-shadow:0 22px 58px var(--ts-shadow-gray-1000-a38),0 0 0 1px var(--ts-shadow-blue-300-a5);animation:fadeUp .25s ease}
.modal-close{position:absolute;top:.8rem;right:.8rem;display:grid;place-items:center;width:30px;height:30px;border:1px solid var(--ts-border-navy-700);border-radius:8px;background:var(--ts-bg-navy-850-34);font-size:1rem;cursor:pointer;color:var(--ts-text-sky-250-19);transition:all .12s ease}
.modal-close:hover{border-color:var(--ts-border-red-350-a50);background:var(--ts-bg-red-350-a12);color:var(--ts-text-red-200-7)}
.novelty-modal-header{display:flex;align-items:center;gap:.8rem;margin:0 2.6rem .8rem 0;padding:0;background:none;border:0;box-shadow:none}
.modal-category-icon{display:grid;place-items:center;width:46px;height:46px;flex:0 0 46px;border:1px solid var(--ts-border-blue-300-a38);border-radius:12px;background:linear-gradient(145deg,var(--ts-bg-blue-300-a20),var(--ts-bg-blue-450-a8));color:var(--ts-text-blue-200-11);font-size:1.15rem;box-shadow:0 8px 20px var(--ts-shadow-blue-500-a16)}
.modal-heading-copy{min-width:0;flex:1;background:none;border:0;box-shadow:none}
.modal-badges{display:flex;gap:.35rem;flex-wrap:wrap}
.modal-title{margin:.3rem 0 0;color:var(--ts-text-blue-0-2);font-size:1.25rem;font-weight:700;line-height:1.3}
.modal-desc{color:var(--ts-text-sky-200-11);font-size:.86rem;line-height:1.55;margin-bottom:.9rem;padding:.75rem .85rem;border:1px solid var(--ts-border-blue-300-a20);border-left:3px solid var(--ts-border-blue-300-3);border-radius:8px;background:var(--ts-bg-navy-850-9)}
.identity-grid,.modal-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.7rem;margin:.7rem 0}
.mc{--detail-accent:var(--ts-text-blue-300-8);min-height:64px;padding:.65rem .75rem;border:1px solid var(--ts-border-navy-700);border-left:3px solid var(--detail-accent);border-radius:8px;display:flex;flex-direction:column;gap:.05rem;background:var(--ts-bg-navy-850-9);transition:border-color .2s ease,transform .2s ease}
.mc:nth-child(4n+2){--detail-accent:var(--ts-accent-cyan-400)}.mc:nth-child(4n+3){--detail-accent:var(--ts-text-indigo-250-4)}.mc:nth-child(4n+4){--detail-accent:var(--ts-accent-teal-400)}
.identity-card{--detail-accent:var(--ts-text-blue-250-18);background:linear-gradient(120deg,var(--ts-bg-blue-250-a12),var(--ts-bg-navy-850-25) 48%)}
.identity-card+ .identity-card{--detail-accent:var(--ts-accent-teal-400)}
.mc:hover{border-color:var(--ts-border-sky-550-4);transform:translateY(-1px)}
.mc span{font-size:.62rem;color:var(--ts-text-sky-350-2);text-transform:uppercase;letter-spacing:.03em;font-weight:600}
.mc span i{font-size:.64rem;margin-right:.2rem;color:var(--detail-accent)}
.mc strong{margin-top:.2rem;color:var(--ts-text-blue-50-6);font-size:.86rem;overflow-wrap:anywhere}
.meta-section{margin-top:.75rem;padding-top:.75rem;border-top:1px solid var(--ts-border-navy-700-2)}
.meta-section h4{font-weight:600;color:var(--ts-text-on-accent-22);font-size:.76rem;margin:0 0 .45rem;display:flex;align-items:center;gap:.3rem}
.meta-grid{padding:.55rem;border:1px solid var(--ts-border-navy-700-2);border-radius:8px;background:var(--ts-bg-navy-850-9)}
.st-unread{color:var(--ts-text-blue-250-19)}.st-read{color:var(--ts-text-sky-200-11)}
.st-resolved,.em-sent{color:var(--ts-text-green-300-5)}
.st-expired{color:var(--ts-text-amber-300-4);opacity:.6}.em-pending{color:var(--ts-text-amber-300-4)}
.em-failed{color:var(--ts-text-red-200-6)}.em-not_requested{color:var(--ts-text-sky-300-17)}
.novelty-modal-actions{
  display:flex;align-items:center;justify-content:flex-end;gap:.65rem;
  position:sticky;bottom:-1.25rem;z-index:3;margin:1rem -1.25rem -1.25rem;padding:.85rem 1.25rem;
  border-top:1px solid var(--ts-border-navy-700-2);background:var(--ts-bg-navy-850-33);box-shadow:0 -10px 24px var(--ts-shadow-gray-1000-a16);
}
.novelty-modal-actions button:disabled{opacity:.62;cursor:not-allowed;transform:none;box-shadow:none}
.act-primary{padding:.5rem .9rem;border:0;border-radius:8px;background:linear-gradient(135deg,var(--ts-bg-blue-400),var(--ts-bg-blue-450));color:var(--ts-on-primary);font-weight:600;font-size:.8rem;cursor:pointer;transition:all .15s ease;display:inline-flex;align-items:center;gap:.3rem}
.act-primary:hover{box-shadow:0 4px 14px var(--ts-shadow-blue-400-a30);transform:translateY(-1px)}
.act-secondary{padding:.5rem .9rem;border:1px solid var(--ts-border-strong);border-radius:8px;background:var(--ts-bg-navy-850-34);color:var(--ts-text-sky-100-3);font-weight:600;font-size:.8rem;cursor:pointer;transition:all .15s ease;display:inline-flex;align-items:center;gap:.3rem}
.act-secondary:hover{background:var(--ts-bg-blue-400-a10);border-color:var(--ts-border-blue-400-a40-3)}

/* Tema claro del modal real mostrado a propietarios. Se mantiene dentro de
   :host-context para no tocar el tema oscuro y, al vivir en el componente,
   se carga después de los estilos globales que antes lo repintaban. */
:host-context(.tusala-light-theme) .modal{color:#10233e!important;border-color:#94a9c3!important;background:#fff!important;box-shadow:0 18px 46px rgba(15,23,42,.18)!important}
:host-context(.tusala-light-theme) .novelty-modal-header{margin:0;padding:1.15rem 3.5rem 1.15rem 1.25rem;color:#fff!important;border-bottom:0!important;background:linear-gradient(120deg,#1d4ed8,#6d28d9)!important;box-shadow:none!important}
:host-context(.tusala-light-theme) .novelty-modal-header :is(.modal-title,.badge,.prio-badge){color:#fff!important}
:host-context(.tusala-light-theme) .modal-category-icon{color:#fff!important;border-color:rgba(255,255,255,.72)!important;background:rgba(255,255,255,.16)!important;box-shadow:none!important}
:host-context(.tusala-light-theme) .modal-close{z-index:2;color:#fff!important;border-color:rgba(255,255,255,.65)!important;background:rgba(255,255,255,.14)!important;box-shadow:none!important}
:host-context(.tusala-light-theme) .modal-desc{margin:1rem 1.25rem;color:#12325b!important;border:1px solid #60a5fa!important;border-left:5px solid #2563eb!important;background:#dbeafe!important;box-shadow:none!important}
:host-context(.tusala-light-theme) .identity-grid,
:host-context(.tusala-light-theme) .modal-grid{margin:.7rem 1.25rem}
:host-context(.tusala-light-theme) .mc{color:#10233e!important;border-color:#a8b8cc!important;border-left-width:5px!important;background:#fff!important;box-shadow:none!important;transform:none!important}
:host-context(.tusala-light-theme) .mc:nth-child(4n+1){border-left-color:#2563eb!important}
:host-context(.tusala-light-theme) .mc:nth-child(4n+2){border-left-color:#0891b2!important}
:host-context(.tusala-light-theme) .mc:nth-child(4n+3){border-left-color:#7c3aed!important}
:host-context(.tusala-light-theme) .mc:nth-child(4n+4){border-left-color:#15803d!important}
:host-context(.tusala-light-theme) .mc span{color:#405574!important}
:host-context(.tusala-light-theme) .mc strong{color:#10233e!important}
:host-context(.tusala-light-theme) .meta-section{border-top-color:#c5d2e3!important}
:host-context(.tusala-light-theme) .meta-section h4{color:#10233e!important}
:host-context(.tusala-light-theme) .meta-grid{border:1px solid #c5d2e3!important;background:#f8fafc!important;box-shadow:none!important}
:host-context(.tusala-light-theme) .novelty-modal-actions{border-color:#c5d2e3!important;background:#fff!important;box-shadow:none!important}

/* El borde pertenece al control completo; el input interior no dibuja otro. */
.search-control{
  position:relative;width:100%;height:40px;display:grid;grid-template-columns:20px minmax(0,1fr);
  align-items:center;gap:.55rem;padding:0 .75rem;
  box-sizing:border-box;border:1px solid var(--ts-border-navy-700);border-radius:9px;background:var(--ts-bg-navy-900);
  color:var(--ts-text-sky-350-24);transition:border-color .15s ease,box-shadow .15s ease;
  overflow:hidden;
}
.search-control:focus-within{border-color:var(--ts-border-blue-300-3);box-shadow:0 0 0 3px var(--ts-shadow-blue-300-a12)}
.search-symbol{display:block;width:18px;height:18px;overflow:visible;fill:none;stroke:var(--ts-text-sky-300-18);stroke-width:1.8;stroke-linecap:round}
.search-control input,.search-control input:hover,.search-control input:focus{
  appearance:none!important;width:100%!important;height:38px!important;min-width:0!important;margin:0!important;
  padding:0!important;border:0!important;border-radius:0!important;outline:0!important;box-shadow:none!important;
  color:var(--ts-text-soft-2)!important;background:transparent!important;
}
.search-control input::-webkit-search-cancel-button{filter:invert(.8)}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@media(max-width:1280px){.filters{grid-template-columns:repeat(4,minmax(0,1fr))}.search-ff{grid-column:span 2}}
@media(max-width:768px){.filters{grid-template-columns:1fr 1fr}.search-ff{grid-column:1/-1}.hero{flex-direction:column;text-align:center}.hero-stats{justify-content:center}.identity-grid,.modal-grid{grid-template-columns:1fr}.novelty-modal-actions{position:static;margin:1rem 0 0;padding:.85rem 0 0;flex-direction:column}.novelty-modal-actions button{width:100%;justify-content:center}}
@media(max-width:520px){.filters{grid-template-columns:1fr}.search-ff{grid-column:auto}.btn-ghost{width:100%}}
  `]
})
export class NovedadesComponent implements OnInit {
  readonly formatLongDate = formatLongDate;
  items = signal<Novelty[]>([]);
  visibleItems = computed(() => this.items());

  loading = signal(false);
  activeRequest = signal<'filter' | 'reset' | null>(null);
  error = signal('');
  unread = computed(() => this.service.businessUnread());
  selected = signal<Novelty | null>(null);
  actionBusy = signal<'resolve' | 'navigate' | null>(null);
  markAllBusy = signal(false);

  search = '';
  status: StatusFilter = '';
  priority: PriorityFilter = '';
  category: CategoryFilter = '';
  from = '';
  to = '';

  page = signal(1);
  pages = signal(0);
  total = signal(0);
  limit = 25;
  readonly pageSizeOptions = [25, 50, 75, 100];

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notifications = inject(NotificationService);
  private readonly theme = inject(ThemeService);

  constructor(
    private readonly service: NoveltyService,
    private readonly auth: AuthService,
    private readonly dialogs: DialogService,
    private readonly cooldown: ActionCooldownService,
  ) {}

  businessId(): number {
    return this.auth.currentUser()?.id || 0;
  }

  ngOnInit(): void {
    const biz = this.businessId();
    if (biz) this.service.startBusinessPolling(biz);
    this.load();
  }

  resetFilters(): void {
    if (this.loading()) return;
    this.search = ''; this.status = ''; this.priority = ''; this.category = '';
    this.from = ''; this.to = '';
    this.page.set(1);
    this.load('reset');
  }

  applyFilters(): void {
    if (!this.cooldown.consume('novedades-filter')) return;
    if (this.loading()) return;
    if (this.from && this.to && this.from > this.to) {
      this.error.set('La fecha inicial no puede ser posterior a la fecha final.');
      this.notifications.error('La fecha inicial no puede ser posterior a la fecha final.');
      return;
    }
    this.page.set(1);
    this.load('filter');
  }

  load(action: 'filter' | 'reset' | null = null): void {
    if (!this.businessId() || this.loading()) return;
    const loadingStartedAt = Date.now();
    this.loading.set(true);
    this.activeRequest.set(action);
    this.error.set('');
    const request = this.service.list({
        business_id: this.businessId(),
        page: this.page(),
        limit: this.limit,
        search: this.search.trim(),
        status: this.status,
        priority: this.priority,
        category: this.category,
        from: this.from,
        to: this.to,
      }).pipe(
        map(response => ({ response, requestError: null })),
        catchError(requestError => of({ response: null, requestError })),
      );

    request.pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        finishLoadingAfter(loadingStartedAt, () => {
          this.loading.set(false);
          this.activeRequest.set(null);
        });
      }),
    ).subscribe({
      next: (result) => {
        if (result.requestError || !result.response) {
          const msg = friendlyError(result.requestError, 'No fue posible cargar novedades.');
          this.error.set(msg);
          this.notifications.error(msg);
          return;
        }
        const r = result.response;
        this.items.set(r.data || []);
        this.pages.set(r.pages);
        this.total.set(r.total);
      },
    });
  }

  goToPage(next: number): void {
    if (this.loading() || next < 1 || next > this.pages() || next === this.page()) return;
    this.page.set(next);
    this.load();
  }

  onPageSizeChange(rawValue: number | string): void {
    const value = Number(rawValue);
    if (!this.pageSizeOptions.includes(value) || value === this.limit || this.loading()) return;
    this.limit = value;
    this.page.set(1);
    this.load('filter');
  }

  open(i: Novelty): void {
    this.selected.set(i);
    if (i.status !== 'unread') return;
    this.service.markRead(i.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.items.update(items => this.status === 'unread'
          ? items.filter(it => it.id !== i.id)
          : items.map(it => it.id === i.id && it.status !== 'resolved'
            ? { ...it, status: 'read' as const, read_at: new Date().toISOString() }
            : it));
        this.selected.update(current => current?.id === i.id && current.status !== 'resolved'
          ? { ...current, status: 'read' as const, read_at: new Date().toISOString() }
          : current);
        if (this.status === 'unread') this.total.update(value => Math.max(0, value - 1));
        this.service.decrementBusinessUnread();
        // Bug real corregido: antes solo se decrementaba el contador TOTAL
        // -- el badge específico del módulo (Reservas/Clientes en el menú
        // lateral) quedaba desfasado hasta que el usuario entraba a ese
        // módulo (lo cual marca TODO como leído de golpe, no solo lo visto).
        this.service.decrementBusinessCategory(i.category);
      },
      error: () => { this.error.set('No fue posible actualizar el estado de lectura.'); this.notifications.error('No fue posible actualizar el estado de lectura.'); },
    });
  }

  close(): void { this.selected.set(null); }

  @HostListener('document:keydown.escape')
  escape(): void { this.close(); }

  async markAll(): Promise<void> {
    if (!this.cooldown.consume('novedades-mark-all')) return;
    const biz = this.businessId();
    if (!biz || this.markAllBusy()) return;
    this.markAllBusy.set(true);
    this.error.set('');
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Marcar todo leído', message: '¿Marcas todas las novedades como leídas?', tone: 'info', confirmText: 'Marcar todo leído' },
        () => this.service.markAllRead(biz),
        'Novedades marcadas como leídas',
      );
      if (result !== null) {
        if (this.status === 'unread') {
          this.items.set([]);
          this.total.set(0);
          this.pages.set(0);
        } else {
          this.items.update(items => items.map(it => it.status === 'unread' ? { ...it, status: 'read' as const, read_at: new Date().toISOString() } : it));
        }
        this.service.setBusinessUnread(0);
        this.service.clearBusinessCategoryCounts();
      }
    } catch {
      this.error.set('No fue posible marcar todas las novedades como leídas.');
      this.notifications.error('No fue posible marcar todas las novedades como leídas.');
    } finally {
      this.markAllBusy.set(false);
    }
  }

  async resolve(i: Novelty): Promise<void> {
    if (!this.cooldown.consume('novedades-resolve')) return;
    if (this.actionBusy() || i.status === 'resolved') return;
    this.actionBusy.set('resolve');
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Marcar como resuelta', message: '¿Confirmas que esta novedad ya fue resuelta?', tone: 'info', confirmText: 'Marcar como resuelta' },
        () => this.service.markResolved(i.id),
        'Novedad marcada como resuelta',
      );
      if (result !== null) {
        if (i.status === 'unread') {
          this.service.decrementBusinessUnread();
          this.service.decrementBusinessCategory(i.category);
        }
        const remainsVisible = !this.status || this.status === 'resolved';
        this.items.update(items => remainsVisible
          ? items.map(it => it.id === i.id ? { ...it, status: 'resolved' as const, read_at: new Date().toISOString() } : it)
          : items.filter(it => it.id !== i.id));
        if (!remainsVisible) this.total.update(value => Math.max(0, value - 1));
        this.close();
      }
    } catch {
      this.error.set('No fue posible marcar la novedad como resuelta. Intenta nuevamente.');
      this.notifications.error('No fue posible marcar la novedad como resuelta. Intenta nuevamente.');
    } finally {
      this.actionBusy.set(null);
    }
  }

  async goTarget(i: Novelty): Promise<void> {
    const module = this.targetModule(i);
    if (!module || this.actionBusy()) return;
    this.actionBusy.set('navigate');
    const path = `/dashboard?module=${encodeURIComponent(module)}`;
    try {
      const navigated = await this.router.navigateByUrl(path);
      if (navigated) this.close();
      else { this.error.set('No fue posible abrir la sección relacionada.'); this.notifications.error('No fue posible abrir la sección relacionada.'); }
    } catch {
      this.error.set('No fue posible abrir la sección relacionada.');
      this.notifications.error('No fue posible abrir la sección relacionada.');
    } finally {
      this.actionBusy.set(null);
    }
  }

  trackById = (_: number, item: Novelty): number => item.id;

  relativeTime(dateStr: string): string {
    try {
      const now = Date.now();
      const date = new Date(dateStr).getTime();
      const diffMs = now - date;
      if (diffMs < 0) return this.fmtDate(new Date(dateStr));
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 1) return 'Ahora';
      if (mins < 60) return `Hace ${mins} min`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `Hace ${hours}h`;
      const days = Math.floor(hours / 24);
      if (days === 1) return 'Ayer';
      if (days < 7) return `Hace ${days} d\u00edas`;
      return this.fmtDate(new Date(dateStr));
    } catch { return dateStr; }
  }

  private fmtDate(d: Date): string {
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  }

  detailDescription(item: Novelty): string {
    const type = item.type;
    if (type === 'booking_created' || type === 'booking_payment_received') {
      return this.normalizeNoveltyText(item.description || item.summary || `Se registr\u00f3 una nueva reserva para ${this.spaceDisplayName(item)}.`);
    }
    if (type === 'booking_cancelled') {
      let desc = item.description || item.summary || `Se cancel\u00f3 una reserva de ${this.spaceDisplayName(item)}.`;
      if (item.reason) desc += ` \u2014 Motivo: ${item.reason}.`;
      return this.normalizeNoveltyText(desc);
    }
    if (type === 'payment_received' || type === 'payment_failed') {
      return this.normalizeNoveltyText(item.description || (type === 'payment_received' ? 'Se proces\u00f3 un pago operativo.' : 'Un pago operativo no pudo completarse.'));
    }
    if (type === 'subscription_renewed' || type === 'subscription_created' || type === 'subscription_plan_changed') {
      return this.normalizeNoveltyText(item.description || item.summary || 'Cambio en la suscripci\u00f3n del negocio.');
    }
    if (type === 'broadcast_announcement') {
      return this.normalizeNoveltyText(item.description || 'Anuncio global de la plataforma TuSala.');
    }
    return this.normalizeNoveltyText(item.description || item.summary || 'Sin descripci\u00f3n adicional.');
  }

  normalizeNoveltyText(value?: string | null): string {
    return String(value || '')
      .replace(/\breservation_date\b/gi, 'fecha de reserva')
      .replace(/\bstart_time\b/gi, 'hora de inicio')
      .replace(/\bend_time\b/gi, 'hora de finalización')
      .replace(/\bnumber_of_people\b/gi, 'cantidad de personas')
      .replace(/\bpayment_status\b/gi, 'estado del pago')
      .replace(/\btotal_price\b/gi, 'total')
      .replace(/\bguest_name\b/gi, 'nombre del cliente')
      .replace(/\bguest_phone\b/gi, 'teléfono del cliente')
      .replace(/\bguest_email\b/gi, 'correo del cliente')
      .replace(/\bextras\b/gi, 'servicios adicionales')
      .replace(/\bplan\s+plan\b/gi, 'Plan')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  formatDetailKeys(meta: Record<string, unknown>): string[] {
    const visible = new Set([
      'action', 'source', 'method', 'plan_name', 'amount', 'status', 'reason',
      'customer_name', 'guest_name', 'reservation_date',
      'start_time', 'end_time', 'payment_method', 'old_plan', 'new_plan',
    ]);
    const order = [
      'customer_name', 'guest_name', 'reservation_date', 'start_time', 'end_time',
      'amount', 'payment_method', 'status', 'reason', 'plan_name', 'old_plan',
      'new_plan', 'action', 'source', 'method',
    ];
    try {
      return order.filter(k => visible.has(k) && meta[k] !== null && meta[k] !== undefined && typeof meta[k] !== 'object');
    }
    catch { return []; }
  }

  formatDetailLabel(key: string): string {
    const labels: Record<string, string> = {
      action: 'Acci\u00f3n realizada', source: 'Origen de la operación',
      method: 'Origen de la operación', plan_name: 'Plan',
      amount: 'Monto', status: 'Estado', reason: 'Motivo',
      customer_name: 'Cliente', guest_name: 'Cliente invitado',
      space_name: 'Espacio', space_title: 'Espacio', reservation_date: 'Fecha de reserva',
      start_time: 'Hora de inicio', end_time: 'Hora de finalizaci\u00f3n',
      payment_method: 'Forma de pago', old_plan: 'Plan anterior', new_plan: 'Plan nuevo',
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatDetailValue(key: string, value: unknown): string {
    if (value === null || value === undefined) return '\u2014';
    if (typeof value === 'boolean') return value ? 'S\u00ed' : 'No';
    if (key === 'source' || key === 'method') {
      return ({
        post: 'Panel administrativo', put: 'Panel administrativo',
        patch: 'Panel administrativo', delete: 'Panel administrativo',
        get: 'Consulta del sistema', admin_panel: 'Panel administrativo',
        super_admin_panel: 'Panel de administración global',
        customer_portal: 'Portal del cliente', system: 'Proceso automático',
        manual: 'Registro manual',
      } as Record<string, string>)[String(value).toLowerCase()] || 'Panel administrativo';
    }
    if (key === 'payment_method') {
      return ({
        apay: 'Pago en l\u00ednea', cash: 'Efectivo', transfer: 'Transferencia',
        card: 'Tarjeta',
      } as Record<string, string>)[String(value).toLowerCase()] || String(value).replace(/_/g, ' ');
    }
    if (key === 'action') return ({
      create: 'Creaci\u00f3n', update: 'Actualizaci\u00f3n', delete: 'Eliminaci\u00f3n',
      renew: 'Renovaci\u00f3n', cancel: 'Cancelaci\u00f3n', plan_change: 'Cambio de plan',
      register: 'Registro', confirm: 'Confirmaci\u00f3n', restore: 'Restauración',
      reset_password: 'Restablecimiento de contraseña',
      change_status: 'Cambio de estado', set_featured_image: 'Cambio de imagen principal',
    } as Record<string, string>)[String(value)] || String(value);
    if (key === 'status') {
      return ({
        pending: 'Pendiente', completed: 'Completado', confirmed: 'Confirmado',
        cancelled: 'Cancelado', failed: 'Fallido', active: 'Activo', inactive: 'Inactivo',
      } as Record<string, string>)[String(value).toLowerCase()] || String(value).replace(/_/g, ' ');
    }
    if (key === 'amount') {
      const amount = Number(value);
      return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '\u2014';
    }
    if (key === 'reservation_date') {
      const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
      return Number.isNaN(date.getTime())
        ? String(value)
        : new Intl.DateTimeFormat('es-SV', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
    }
    return this.normalizeNoveltyText(String(value).replace(/_/g, ' '));
  }

  private metadataText(item: Novelty, ...keys: string[]): string {
    for (const key of keys) {
      const value = item.metadata?.[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  spaceDisplayName(item: Novelty): string {
    return this.metadataText(item, 'space_name', 'space_title') || 'Espacio del negocio';
  }

  reservationDisplayName(item: Novelty): string {
    const reference = this.metadataText(item, 'reservation_reference');
    return [reference].filter(Boolean).join(' · ') || 'Reserva del negocio';
  }

  paymentDisplayName(item: Novelty): string {
    if (item.subscription_id || item.category === 'subscriptions') return 'Pago de suscripción';
    if (item.reservation_id) return `Pago de la reserva ${this.reservationDisplayName(item)}`;
    const space = this.metadataText(item, 'space_name', 'space_title');
    const customer = this.metadataText(item, 'customer_name', 'guest_name');
    return space ? `Pago de ${space}` : customer ? `Pago de ${customer}` : 'Pago del negocio';
  }

  subscriptionDisplayName(item: Novelty): string {
    const plan = this.metadataText(item, 'plan_name', 'plan', 'new_plan');
    return plan ? this.normalizeNoveltyText(`Plan ${plan}`) : 'Suscripci\u00f3n del negocio';
  }

  actionButtonLabel(label?: string | null): string {
    const normalized = (label || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
    const labels: Record<string, string> = {
      'ver reserva': 'Ver reserva', 'ver reservas': 'Ver reservas',
      'ver cliente': 'Ver cliente', 'ver clientes': 'Ver clientes',
      'ver espacio': 'Ver espacio', 'ver espacios': 'Ver espacios',
      'ver pago': 'Ver pago', 'ver pagos': 'Ver pagos',
      'ver suscripcion': 'Ver suscripci\u00f3n', 'ver suscripciones': 'Ver suscripciones',
      reservations: 'Ver reservas', customers: 'Ver clientes', spaces: 'Ver espacios',
      payments: 'Ver pagos', subscriptions: 'Ver suscripciones',
    };
    return labels[normalized] || 'Abrir secci\u00f3n relacionada';
  }

  targetModule(item: Novelty): string | null {
    if (item.category === 'reservations') return 'reservas';
    if (item.category === 'customers') return 'usuarios';
    if (item.category === 'spaces') return 'salas';
    if (item.category === 'subscriptions' || item.category === 'owners') return 'suscripcion';
    if (item.category === 'employees') return 'subadministrador';
    if (item.category === 'settings') return 'configuracion';
    if (item.category === 'payments') {
      if (item.reservation_id) return 'reservas';
      if (item.subscription_id) return 'suscripcion';
    }

    const rawPath = String(item.target_path || '');
    const match = rawPath.match(/[?&](?:module|tab)=([^&]+)/i);
    if (!match) return null;
    const legacy = decodeURIComponent(match[1]).toLowerCase();
    return ({
      reservas: 'reservas', reservations: 'reservas',
      clientes: 'usuarios', customers: 'usuarios',
      espacios: 'salas', spaces: 'salas',
      subscriptions: 'suscripcion', subscription: 'suscripcion',
      configuracion: 'configuracion', settings: 'configuracion',
      empleados: 'subadministrador', employees: 'subadministrador',
    } as Record<string, string>)[legacy] || null;
  }

  targetActionLabel(item: Novelty): string {
    return ({
      reservas: item.reservation_id ? 'Ver reserva' : 'Ver reservas',
      usuarios: 'Ver clientes',
      salas: 'Ver espacios',
      suscripcion: 'Ver mi suscripción',
      subadministrador: 'Ver subadministradores',
      configuracion: 'Ver configuración',
    } as Record<string, string>)[this.targetModule(item) || ''] || this.actionButtonLabel(item.action_label);
  }

  emailStatusLabel(s: string): string {
    return ({ sent: 'Enviado', pending: 'Pendiente de envío', failed: 'No se pudo enviar', not_requested: 'No requiere notificación' } as Record<string, string>)[s] || s;
  }

  shouldShowEmailStatus(item: Novelty): boolean {
    return !!item.email_status && item.email_status !== 'not_requested';
  }

  priorityLabel(p: string): string {
    return ({ critical: 'Cr\u00edtica', high: 'Alta', normal: 'Normal', low: 'Baja' } as Record<string, string>)[p] || p;
  }

  categoryLabel(c: string): string {
    return ({
      reservations: 'Reservas', payments: 'Pagos', subscriptions: 'Suscripciones',
      customers: 'Clientes', spaces: 'Espacios', settings: 'Ajustes',
      employees: 'Empleados', administration: 'Administraci\u00f3n', owners: 'Propietarios',
    } as Record<string, string>)[c] || c;
  }

  categoryIcon(category: string): string {
    return ({
      reservations: 'bi bi-calendar-check', payments: 'bi bi-credit-card',
      subscriptions: 'bi bi-gem', customers: 'bi bi-people',
      spaces: 'bi bi-building', settings: 'bi bi-gear',
      employees: 'bi bi-person-gear', administration: 'bi bi-shield-check',
      owners: 'bi bi-person-badge',
    } as Record<string, string>)[category] || 'bi bi-megaphone';
  }

  // Mismo acento por categoría que ya usaba la tarjeta anterior
  // (.nv-card.category-*), ahora aplicado como color inline del ícono.
  private readonly categoryColors: Record<string, string> = {
    reservations: '#62a0ff', payments: '#4ed39a', subscriptions: '#ad87ff',
    customers: '#efbd5e', spaces: '#52cee8', settings: '#a6b7ca', employees: '#a6b7ca',
    administration: '#ff858e', owners: '#f59a63',
  };

  categoryColor(category: string): string {
    return toneForTheme(this.categoryColors[category] || '#5f9bff', this.theme.theme());
  }

  categoryColorSoft(category: string): string {
    const hex = this.categoryColor(category);
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, .14)`;
  }

  statusLabel(s: string): string {
    return ({ unread: 'No le\u00edda', read: 'Le\u00edda', resolved: 'Resuelta', expired: 'Expirada' } as Record<string, string>)[s] || s;
  }
}
